import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useMediaQuery } from "react-responsive";
import axios from "axios";
import { toast } from "react-toastify";

import { BsPin, BsPinAngleFill } from "react-icons/bs";
import { RxDotsVertical } from "react-icons/rx";
import { HiOutlineMenu } from "react-icons/hi";
import { PiUsersThree } from "react-icons/pi";

import { ProjectInfoContainer } from "../../common/styled/project";
import { isSameUser } from "../../../../scheduler/component/function/common";
import { getProjectColor } from "../../common/function/project";
import useOutsideClick from "../../../../../../hooks/useOutsideClick";

// 즐겨찾기 버튼
const PinButton = ({ project, onToggle }) => {
  if (!project?.projectParticipantNo) return null;

  return (
    <button onClick={onToggle}>
      {project.pinnedYn === "Y" ? <BsPinAngleFill style={{ color: "#e72e2e" }} /> : <BsPin />}
    </button>
  );
};

// 메뉴 버튼
const ActionMenu = ({ openMenu, setOpenMenu, onEdit, onComplete, onDelete, isComplete }) => {
  const tabRef = useRef();
  useOutsideClick(tabRef, () => {
    setOpenMenu(false);
  });

  const menuItems = [
    { label: isComplete ? "미완료" : "완료", onClick: onComplete },
    { label: "수정", onClick: onEdit },
    { label: "삭제", onClick: onDelete },
  ];

  return (
    <div className="actions" ref={tabRef}>
      <button onClick={() => setOpenMenu((prev) => !prev)}>
        <RxDotsVertical />
      </button>
      {openMenu && (
        <ul>
          {menuItems.map(({ label, onClick }, idx) => (
            <li
              key={idx}
              onClick={() => {
                onClick();
                setOpenMenu(false);
              }}>
              {label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Info = ({
  project = {},
  setProject,
  openMenu,
  setOpenMenu,
  projectInfo = {},
  setShowSideMenu,
  setShowParticipants,
  participant = {},
  openModal,
  closeModal,
}) => {
  const navigate = useNavigate();
  const isTablet = useMediaQuery({ query: "(max-width:1050px)" });
  const isMobile = useMediaQuery({ query: "(max-width:767px)" });

  const user = useSelector((state) => state?.user?.data?.[0]);
  const company = useSelector((state) => state?.company?.data || []);

  const isOwner = isSameUser(user, project);

  const handleDeleteProject = async () => {
    try {
      await axios.put("/api/project", { ...projectInfo, delYn: "Y" });
      toast.success("프로젝트가 삭제되었습니다.", { toastId: "no-access" });
      navigate("/mypage/projectList");
    } catch (error) {
      console.error(error);
      toast.error("잠시 후 다시 시도해주세요.");
    } finally {
      closeModal();
    }
  };

  const handleTogglePin = async () => {
    if (project.completeYn === "Y") {
      toast.error("해당 프로젝트는 변경할 수 없습니다.");
      return;
    }

    const pinnedYn = project.pinnedYn === "Y" ? "N" : "Y";
    const payload = [
      {
        projectParticipantNo: project.projectParticipantNo,
        projectNo: project.projectNo,
        gbn: project.gbn,
        mberNo: user.mberNo,
        orgNo: project.gbn === "C" ? participant.cmpnyNo : project.orgNo,
        partiType: project.partiType,
        pinnedYn,
        delYn: "N",
        updateMberId: user.userName,
      },
    ];

    try {
      await axios.put("/api/participants", payload);
      setProject((prev) => ({ ...prev, pinnedYn }));
      toast.success(`즐겨찾기가 ${pinnedYn === "Y" ? "등록" : "취소"}되었습니다.`);
    } catch (error) {
      console.error(error);
      toast.error("즐겨찾기 변경 중 오류가 발생했습니다.");
    }
  };

  const handleEdit = () => {
    navigate("/mypage/projectCreateSec", { state: { projectInfo } });
  };

  const handleComplete = async () => {
    try {
      const updatedCompleteYn = project.completeYn === "N" ? "Y" : "N";

      await axios.put("/api/project", {
        ...projectInfo,
        completeYn: updatedCompleteYn,
      });

      setProject((prev) => ({ ...prev, completeYn: updatedCompleteYn }));
      toast.success(
        `프로젝트가 ${
          updatedCompleteYn === "Y" ? "완료되었습니다." : "진행 상태로 변경되었습니다."
        }`,
      );
    } catch (error) {
      console.error(error);
      toast.error("잠시 후 다시 시도해주세요.");
    } finally {
      closeModal();
    }
  };

  const handleDelete = () => {
    openModal({
      isOpen: true,
      type: "confirmation",
      title: "삭제",
      message: ["프로젝트를 삭제하시겠습니까?"],
      handleConfirm: handleDeleteProject,
      handleCancel: closeModal,
    });
  };

  return (
    <ProjectInfoContainer>
      {isTablet && (
        <button onClick={() => setShowSideMenu((prev) => !prev)}>
          <HiOutlineMenu />
        </button>
      )}
      <div className="gbn" style={{ background: getProjectColor(project, company) }} />
      <PinButton project={project} onToggle={handleTogglePin} />

      {isOwner && (
        <ActionMenu
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onComplete={handleComplete}
          isComplete={project.completeYn === "Y"}
        />
      )}

      <div className="tit">
        <h2
          style={{
            textDecoration: project.completeYn === "Y" ? "line-through" : "none",
          }}>
          {project?.projectNm || ""}
        </h2>
        {project?.projectDesc && <p title={project.projectDesc}>{project.projectDesc}</p>}
      </div>

      {isMobile && (
        <button onClick={() => setShowParticipants((prev) => !prev)} className="members">
          <PiUsersThree />
        </button>
      )}
    </ProjectInfoContainer>
  );
};

export default Info;
